import { useCallback, useRef, useEffect } from "react";
import dayjs from "dayjs";
import db from "../../../../database";
import { showKDSNotification } from "../showKDSNotification";
import useCartStore from "../../../../stores/useCartStore";
import usePOSConfigStore from "../../../../stores/usePOSConfigStore";
import { useWebSocket } from "../../../../lib/components/WebSocketProvider";
import useAuthStore from "../../../../stores/useAuthStore";
import useSessionStore from "../../../../stores/useSessionStore";
import useRestaurantStore from "../../../../stores/useRestaurantStore";
import { upsertTableStatus } from "../../../../services/restaurantServices";
import { wasRecentlyFiredLocally } from "../../../../utils/localFireTracker";
import { trace } from "../../../../utils/debugTrace";
import { roundTo } from "../../../../utils/tillHelpers";
import { trackPendingMirrorOrder } from "../../../../utils/dataIntegrity";
import { closeStaleSessionsKeepingPreparedBills } from "../../../../utils/preparedBillTableLock";

/**
 * Sets up the WebSocket message handler and connects the restaurant WebSocket.
 * Handles TABLE_STATUS_UPDATE, ORDER_UPSERT, NEW_KOT, POS_ORDER_UPSERT, etc.
 */
const useWebSocketHandler = ({ tillData, setTableCards, setOcupiedTablesData, setReservedTablesData, setTableList, setShowProducts, setKeyValue, setCart, precision }) => {
  const processedTableUpdateIdsRef = useRef(new Set());
  // Tracks tables whose order was just completed (CO/CL). Used to suppress
  // stale TABLE_STATUS_UPDATE(OCU) events the server may broadcast during the
  // billing/payment flow even after the order is closed.
  const recentlyCompletedTablesRef = useRef(new Map());

  const handleWebSocketMessage = useCallback(async (message) => {
    try {
      switch (message.type) {
        case "TABLE_STATUS_UPDATE":
        case "TABLE_UPDATE":
          if (message?.data) {
            const updateId = `${message.data?.fbTableId}_${message.data?.status}_${message.timestamp || Date.now()}`;

            if (processedTableUpdateIdsRef.current.has(updateId)) {
              console.debug(`[v0] [HandleMessage] Skipping duplicate table update: ${message.data?.fbTableId}`);
              return;
            }

            const fbTableIdForStatusUpdate = message.data.fbTableId;
            // [BUG-mesh-table-status] Step-by-step trace — remove once the root cause of
            // the missed-lookup gap is confirmed and fixed.
            console.debug(`[BUG-mesh-table-status] TABLE_STATUS_UPDATE received`, {
              fbTableId: fbTableIdForStatusUpdate,
              fbTableIdType: typeof fbTableIdForStatusUpdate,
              status: message.data.status,
            });
            const tableForStatusUpdate = await db.tableData.where("cwrFbTableId").equals(fbTableIdForStatusUpdate).first();
            console.debug(`[BUG-mesh-table-status] TABLE_STATUS_UPDATE lookup result`, {
              fbTableId: fbTableIdForStatusUpdate,
              found: !!tableForStatusUpdate,
            });

            if (!tableForStatusUpdate) {
              // [BUG-mesh-table-status] Diagnostic only — this gate is why a live table
              // status push can silently fail to update the grid (see ORDER_UPSERT's
              // matching gate below). Logging the incoming id/type against a sample of
              // what's actually stored locally to catch the mismatch on the next repro.
              const localSample = await db.tableData.limit(5).toArray();
              console.warn(
                `[v0] [HandleMessage] Table ${fbTableIdForStatusUpdate} not found, skipping TABLE_STATUS_UPDATE`,
                {
                  incomingId: fbTableIdForStatusUpdate,
                  incomingIdType: typeof fbTableIdForStatusUpdate,
                  localTableCount: await db.tableData.count(),
                  localSampleIds: localSample.map((t) => ({ id: t.cwrFbTableId, type: typeof t.cwrFbTableId })),
                }
              );
              break;
            }

            // Suppress OCU events for tables whose order was just completed.
            // The server's billing flow broadcasts OCU events even after a CO/CL
            // order, causing the table to flip back to "Order Placed" with no items.
            if (message.data.status === "OCU") {
              const completedAt = recentlyCompletedTablesRef.current.get(fbTableIdForStatusUpdate);
              const ageMs = completedAt ? Date.now() - completedAt : null;
              console.debug(`[TABLE_STATUS_UPDATE] OCU received for table: ${fbTableIdForStatusUpdate} | recentlyCompleted: ${!!completedAt} | ageMs: ${ageMs}`);
              if (completedAt && ageMs < 30000) {
                console.warn(`[TABLE_STATUS_UPDATE] ⛔ Suppressing stale OCU — table was completed ${ageMs}ms ago: ${fbTableIdForStatusUpdate}`);
                break;
              }
            }

            // Before applying OPN, verify no active (IP) orders exist on this table.
            // A delayed or out-of-order notification about a finished order must not
            // overwrite a table that currently has a newer, active order on it.
            let effectiveStatus = message.data.status;
            if (effectiveStatus === "OPN") {
              const activeOrdersOnTable = await db.fbOrderData
                .where("cwrFbTableId")
                .equals(fbTableIdForStatusUpdate)
                .toArray();
              const hasActiveOrders = activeOrdersOnTable.some((o) => o.fbOrderStatus === "IP");
              if (hasActiveOrders) {
                console.warn(`[TABLE_STATUS_UPDATE] Blocking OPN — table ${fbTableIdForStatusUpdate} still has active IP orders`);
                effectiveStatus = "ONG";
              }
            }

            const statusUpdatePayload = {
              statusType: effectiveStatus,
              tabbleSync: "Y",
              color: effectiveStatus === "OCU" || effectiveStatus === "ONG" ? "#bc4749" : effectiveStatus === "RES" ? "#f2e8cf" : effectiveStatus === "STD" ? "#978ea1" : "rgba(146, 144, 152, 0.5)",
              merge: effectiveStatus === "OPN" && message.data.merges?.length > 0 ? [] : message.data.merges || [],
              guestName: message.data.guestName || "",
            };
            if (effectiveStatus === "OPN") statusUpdatePayload.ordered = "N";
            if (effectiveStatus === "OCU" || effectiveStatus === "ONG") statusUpdatePayload.ordered = "Y";
            if (effectiveStatus === "STD") {
              statusUpdatePayload.startedAt = dayjs().format("YYYY-MM-DD HH:mm");
            }
            if (message.data.salesRepId) {
              statusUpdatePayload.waiter = message.data.salesRepId;
            }
            if (message.data.noOfPersons !== undefined) {
              statusUpdatePayload.noOfPersons = message.data.noOfPersons;
            }
            if (message.data.guestType !== undefined) {
              statusUpdatePayload.guestType = message.data.guestType;
            }

            await db.tableData.update(fbTableIdForStatusUpdate, statusUpdatePayload);

            const updatedTableData = await db.tableData.toArray();
            setTableCards(updatedTableData);
            // [BUG-mesh-table-status] Step-by-step trace — remove once the root cause of
            // the missed-lookup gap is confirmed and fixed.
            console.debug(`[BUG-mesh-table-status] TABLE_STATUS_UPDATE applied — setTableCards called`, {
              fbTableId: fbTableIdForStatusUpdate,
              newStatusType: effectiveStatus,
              tableCardCount: updatedTableData.length,
            });

            const ocuTables = updatedTableData.filter((table) => table.statusType === "OCU" || table.statusType === "ONG");
            setOcupiedTablesData(ocuTables);

            const resTables = updatedTableData.filter((table) => table.statusType === "RES");
            setReservedTablesData(resTables);

            setTableList(updatedTableData);

            processedTableUpdateIdsRef.current.add(updateId);
            if (processedTableUpdateIdsRef.current.size > 500) {
              const entries = Array.from(processedTableUpdateIdsRef.current);
              processedTableUpdateIdsRef.current = new Set(entries.slice(-500));
            }
          }
          break;

        case "SUBSCRIBED":
          console.debug("[v0] [HandleMessage] SUBSCRIBED to bunit:", message.bunitId);
          break;

        case "ORDER_UPSERT":
          console.debug("[v0] [HandleMessage] Processing ORDER_UPSERT");

          if (message && message.data) {
            const orderId = message?.data?.fbOrderId || message.orderId;

            if (!orderId) {
              console.warn("[v0] [HandleMessage] ORDER_UPSERT missing orderId");
              break;
            }

            const orderUpdateId = `${orderId}_${message.timestamp || Date.now()}`;

            if (processedTableUpdateIdsRef.current.has(orderUpdateId)) {
              console.debug(`[v0] [HandleMessage] Skipping duplicate order update: ${orderId}`);
              return;
            }

            try {
              const fbOrderId = message?.data?.fbOrderId;
              const fbTableId = message?.data?.fbTableId;
              // [BUG-mesh-table-status] Step-by-step trace — remove once the root cause of
              // the missed-lookup gap is confirmed and fixed.
              console.debug(`[BUG-mesh-table-status] ORDER_UPSERT received`, {
                fbOrderId,
                fbTableId,
                fbTableIdType: typeof fbTableId,
                status: message?.data?.status,
              });
              const existingTable = await db.tableData.where("cwrFbTableId").equals(fbTableId).first();
              console.debug(`[BUG-mesh-table-status] ORDER_UPSERT existingTable lookup result`, {
                fbTableId,
                found: !!existingTable,
              });
              const existingOrder = await db.fbOrderData.where("fbOrderId").equals(fbOrderId).first();

              // Safely parse wsData from meta: filter by key="cartDetails", guard JSON.parse.
              // Each KOT appends a cumulative cartDetails snapshot, so we must pick the LAST
              // entry (most recent) — meta.find() would return the oldest snapshot and silently
              // drop any items added in subsequent KOTs.
              const safeParseWsData = (meta, status) => {
                if (!meta?.length || status === "CL" || status === "CO") return {};
                const cartMetas = meta.filter((m) => m.key === "cartDetails");
                const cartMeta = cartMetas.length > 0 ? cartMetas[cartMetas.length - 1] : null;
                if (!cartMeta?.value) return {};
                try {
                  return JSON.parse(cartMeta.value) ?? {};
                } catch {
                  console.warn("[v0] ORDER_UPSERT: Failed to parse cartDetails JSON");
                  return {};
                }
              };

              const wsData = safeParseWsData(message?.data?.meta, message?.data?.status);
              const safeLines = (message?.data?.lines?.length ?? 0) > 0 ? message.data.lines : [];
              // [BUG-1921] Full snapshot of what this broadcast actually carried, taken before
              // any of the reconciliation/merge logic below touches it — the ground truth to
              // compare against if a cancelled quantity is still showing up on Prepare Bill.
              trace("BUG-1921", "ORDER_UPSERT received", {
                fbOrderId,
                fbTableId,
                status: message?.data?.status,
                wsDataItems: (wsData?.items || []).map((i) => ({ fbOrderLineId: i.fbOrderLineId, lineId: i.lineId, name: i.name, weight: i.weight, isCanceled: i.isCanceled, order: i.order })),
                linesFromServer: safeLines.map((l) => ({ fbOrderLineId: l.fbOrderLineId, qty: l.qty, isCanceled: l.isCanceled })),
              });
              if (existingOrder) {
                const localStatus = existingOrder.fbOrderStatus;
                const serverStatus = message.data.status;
                if ((localStatus === "CL" || localStatus === "CO") && serverStatus === "IP") {
                  console.debug("[v0] ORDER_UPSERT: ignoring stale IP for locally", localStatus, "order", fbOrderId);
                  processedTableUpdateIdsRef.current.add(orderUpdateId);
                  return;
                }
                await db.fbOrderData.update(existingOrder.id, {
                  fbOrderStatus: message.data.status,
                  name: existingTable?.name || existingTable?.table,
                  lines: safeLines,
                  wsData,
                });
                console.debug("[v0] ORDER_UPSERT: updated existing fbOrderData", fbOrderId);
                if (serverStatus === "CO" || serverStatus === "CL") {
                  // toArray + per-id update: .filter() closures capturing fbOrderId are
                  // lost over the SQLite IPC bridge and would silently close nothing.
                  const sameTableOrders = await db.fbOrderData.where("cwrFbTableId").equals(fbTableId).toArray();
                  const staleIp = sameTableOrders.filter((r) => r.fbOrderStatus === "IP" && r.fbOrderId !== fbOrderId && !r.splitTable);
                  await closeStaleSessionsKeepingPreparedBills(staleIp, fbTableId, fbOrderId);
                }
              } else {
                await db.fbOrderData.add({
                  fbOrderId,
                  fbOrderStatus: message.data.status,
                  fbOrderSync: "Y",
                  cwrFbTableId: message.data.fbTableId,
                  lines: message.data.lines,
                  name: existingTable?.name || existingTable?.table,
                  wsData,
                });
                console.debug("[v0] ORDER_UPSERT: inserted new fbOrderData", fbOrderId, message.data.lines);
                if (message.data.status === "CO" || message.data.status === "CL") {
                  const sameTableOrders = await db.fbOrderData.where("cwrFbTableId").equals(fbTableId).toArray();
                  const staleIp = sameTableOrders.filter((r) => r.fbOrderStatus === "IP" && r.fbOrderId !== fbOrderId && !r.splitTable);
                  await closeStaleSessionsKeepingPreparedBills(staleIp, fbTableId, fbOrderId);
                }
              }

              const allIpOrdersForTable = (await db.fbOrderData.where("cwrFbTableId").equals(fbTableId).toArray()).filter(
                (r) => r.fbOrderStatus === "IP"
              );

              const mergedItems = [...(wsData?.items ?? [])];
              const seenMergedLineIds = new Set(mergedItems.map((i) => i.lineId).filter(Boolean));
              for (const ipOrder of allIpOrdersForTable) {
                if (ipOrder.fbOrderId === fbOrderId) continue;
                for (const item of (ipOrder.wsData?.items || [])) {
                  if (item.lineId && !seenMergedLineIds.has(item.lineId)) {
                    seenMergedLineIds.add(item.lineId);
                    mergedItems.push(item);
                  }
                }
              }

              // [BUG-1921] wsData.items is parsed from the LAST "cartDetails" meta entry
              // (see safeParseWsData above), and that meta is only appended on Fire. A
              // cancel-only update (no accompanying Fire — e.g. cancelling a quantity from
              // the Mobile App) never writes a fresh cartDetails snapshot, so wsData.items
              // can still show the pre-cancel qty/isCanceled even though safeLines
              // (message.data.lines, this order's per-line authoritative state) already has
              // the correction. handleTable already reconciles this exact staleness against
              // order.lines on table REOPEN (useOrderHandlers.js "Reconcile each item's qty
              // from server lines[]") — this mirrors that same reconciliation here so a
              // cancellation that arrives while the table is already open (Prepare Bill's
              // scenario) isn't lost to the same stale-snapshot gap.
              const lineQtyMap = new Map(safeLines.filter((l) => l.fbOrderLineId).map((l) => [l.fbOrderLineId, { qty: Number(l.qty) || 0, isCanceled: l.isCanceled || "N" }]));
              if (lineQtyMap.size > 0) {
                for (let i = 0; i < mergedItems.length; i++) {
                  const item = mergedItems[i];
                  if (!item.fbOrderLineId) continue;
                  const serverLine = lineQtyMap.get(item.fbOrderLineId);
                  if (!serverLine) continue;
                  const before = Number(item.weight) || 0;
                  const after = serverLine.qty;
                  if (before === after && item.isCanceled === serverLine.isCanceled) continue;
                  trace("BUG-1921", "ORDER_UPSERT: reconciling stale cartDetails snapshot against lines[]", {
                    fbOrderLineId: item.fbOrderLineId, before, after, wasCanceled: item.isCanceled, nowCanceled: serverLine.isCanceled,
                  });
                  // Rescale money fields so amount stays a deterministic function of the
                  // corrected qty — identical rescale to handleTable's, for the same reason.
                  const ratio = after !== before && before > 0 ? after / before : 1;
                  mergedItems[i] = {
                    ...item,
                    weight: after,
                    nettotal: parseFloat(((Number(item.nettotal) || 0) * ratio).toFixed(precision)),
                    taxAmount: parseFloat(((Number(item.taxAmount) || 0) * ratio).toFixed(precision)),
                    discount: parseFloat(((Number(item.discount) || 0) * ratio).toFixed(precision)),
                    isCanceled: serverLine.isCanceled,
                  };
                }
              }
              trace("BUG-1921", "ORDER_UPSERT: mergedItems after lines[] reconciliation", {
                mergedItems: mergedItems.map((i) => ({ fbOrderLineId: i.fbOrderLineId, lineId: i.lineId, name: i.name, weight: i.weight, isCanceled: i.isCanceled, order: i.order })),
              });

              const mergedWsData = { ...wsData, items: mergedItems };

              if (existingTable) {
                // Only set table to OPN if no other active (IP) orders remain on it.
                // allIpOrdersForTable is queried AFTER the force-close above, so it
                // reflects only genuinely active orders.
                const hasRemainingActiveOrders = allIpOrdersForTable.length > 0;
                const newStatusType =
                  message.data.status === "IP" ? "ONG" :
                  (message.data.status === "CO" || message.data.status === "CL") && !hasRemainingActiveOrders ? "OPN" :
                  (message.data.status === "CO" || message.data.status === "CL") && hasRemainingActiveOrders ? "ONG" :
                  existingTable.statusType;

                // Track completion so TABLE_STATUS_UPDATE(OCU) events from the
                // server's billing flow are suppressed for 30 s after payment.
                if (message.data.status === "CO" || message.data.status === "CL") {
                  recentlyCompletedTablesRef.current.set(fbTableId, Date.now());
                  console.debug(`[ORDER_UPSERT] Table marked as recently completed — OCU WS events will be suppressed for 30s: ${fbTableId}`);

                  // If the table was occupied and no active orders remain, fix the server's
                  // tableStatus to OPN. Skip if other IP orders still exist on this table.
                  const wasOccupied = existingTable.statusType === "OCU" || existingTable.statusType === "ONG" || existingTable.statusType === "BRQ" || existingTable.statusType === "FSM";
                  const csBunitId = tillData?.tillAccess?.csBunit?.csBunitId;
                  if (wasOccupied && !hasRemainingActiveOrders && existingTable.cwrFbsectionId && csBunitId) {
                    const freeParams = {
                      query: `mutation { upsertTableStatus(tableStatus: {
                        cSBunitID: "${csBunitId}"
                        fbtableStatusId: ${existingTable.fbtableStatusId ? `"${existingTable.fbtableStatusId}"` : null}
                        fbTableId: "${existingTable.cwrFbTableId}"
                        fbSectionId: "${existingTable.cwrFbsectionId}"
                        status: "OPN"
                        guests: 0
                        guestName: ""
                        guestType: ""
                        referredBy: ""
                        salesRepId: null
                        merge: []
                        splitTables: []
                      }) { status message } }`,
                    };
                    console.debug(`[ORDER_UPSERT] Auto-freeing server tableStatus to OPN for completed order: ${fbTableId}`);
                    upsertTableStatus(freeParams).catch((e) => console.debug("[ORDER_UPSERT] Auto-free tableStatus failed:", e));
                  }
                } else if (message.data.status === "IP") {
                  // Table re-occupied with a new order — clear the guard.
                  recentlyCompletedTablesRef.current.delete(fbTableId);
                  console.debug(`[ORDER_UPSERT] Table re-occupied (IP) — OCU suppression cleared: ${fbTableId}`);
                }

                await db.tableData.update(existingTable.cwrFbTableId, {
                  lines: message.data.status !== "CO" ? safeLines : [],
                  items: mergedItems,
                  wsData: mergedWsData,
                  statusType: newStatusType,
                });

                const updatedTableData = await db.tableData.toArray();
                setTableCards(updatedTableData);
                // [BUG-mesh-table-status] Step-by-step trace — remove once the root cause of
                // the missed-lookup gap is confirmed and fixed.
                console.debug(`[BUG-mesh-table-status] ORDER_UPSERT applied — setTableCards called`, {
                  fbTableId,
                  newStatusType,
                  tableCardCount: updatedTableData.length,
                });
                const ocuTables = updatedTableData.filter((t) => t.statusType === "OCU" || t.statusType === "ONG");
                setOcupiedTablesData(ocuTables);
                const resTables = updatedTableData.filter((t) => t.statusType === "RES");
                setReservedTablesData(resTables);
                setTableList(updatedTableData);
              } else {
                // [BUG-mesh-table-status] This is the silent half of the gap: the order
                // itself was still written to fbOrderData above (unconditionally), but
                // without a local tableData row the grid's statusType is never touched —
                // this table stays stale on the grid until a manual navigation forces a
                // full re-read. Same diagnostic shape as TABLE_STATUS_UPDATE's gate.
                const localSample = await db.tableData.limit(5).toArray();
                console.warn(
                  `[v0] [HandleMessage] ORDER_UPSERT: table ${fbTableId} not found locally — order saved but table status NOT updated`,
                  {
                    fbOrderId,
                    incomingId: fbTableId,
                    incomingIdType: typeof fbTableId,
                    localTableCount: await db.tableData.count(),
                    localSampleIds: localSample.map((t) => ({ id: t.cwrFbTableId, type: typeof t.cwrFbTableId })),
                  }
                );
              }

              processedTableUpdateIdsRef.current.add(orderUpdateId);
              if (processedTableUpdateIdsRef.current.size > 500) {
                processedTableUpdateIdsRef.current = new Set([...processedTableUpdateIdsRef.current].slice(-500));
              }

              const tableData = useRestaurantStore.getState().tableName;
              // Normalize both sides before comparing — cwrFbTableId/fbTableId have been
              // observed to travel as different types depending on origin (local IndexedDB
              // read vs. server broadcast), and a raw === here would silently defeat the
              // match with no fallback, leaving the open cart stale with no way to recover.
              const isCurrentlyOpenTable =
                tableData && String(tableData?.cwrFbTableId ?? "") === String(message?.data?.fbTableId ?? "") && tableData?.cwrFbTableId != null;
              if (isCurrentlyOpenTable) {
                if (mergedItems.length > 0) {
                  const currentCart = useCartStore.getState().cart;
                  const currentItems = currentCart?.items || [];
                  const serverLineIds = new Set(mergedItems.map((i) => i.lineId).filter(Boolean));
                  // [BUG-1791] fbOrderLineId is the server's stable identity for a fired
                  // order line; lineId is NOT — mobile mints a fresh lineId on every
                  // cartDetails snapshot even when the line itself didn't change (confirmed
                  // via [BUG-1791] diagnostic capture: same fbOrderLineId, new lineId on each
                  // subsequent Fire). Matching on lineId alone made every already-fired item
                  // fail to match the new snapshot and get re-appended as a duplicate on each
                  // later fire. Dedupe on fbOrderLineId when the item has been fired; only
                  // fall back to lineId for items that haven't been fired yet (no
                  // fbOrderLineId), which mergedItems can never contain anyway.
                  const serverFbOrderLineIds = new Set(mergedItems.map((i) => i.fbOrderLineId).filter(Boolean));
                  // Keep locally-fired items (order === "Y") that the server snapshot
                  // doesn't include. A WS snapshot can lag or originate from a terminal
                  // that never saw this fire — dropping fired items here made them
                  // silently vanish from the order and the bill.
                  const localOnlyItems = currentItems.filter((i) =>
                    i.fbOrderLineId ? !serverFbOrderLineIds.has(i.fbOrderLineId) : !i.lineId || !serverLineIds.has(i.lineId)
                  );
                  // A WS snapshot can also lag on an item present on BOTH sides (same
                  // fbOrderLineId), not just be missing one — the sibling gap the BUG-1791
                  // fix above didn't cover. Cancel → restore → re-fire all happen locally
                  // and synchronously; if an ORDER_UPSERT broadcast for the pre-restore
                  // state arrives after that (a lagging echo, same risk called out at line
                  // 304), its stale isCanceled:"Y"/orderStatus:"CL" would otherwise silently
                  // overwrite the live cart's just-restored, actively-fired line with no
                  // user action.
                  //
                  // [BUG-1921] The original version of this guard trusted "local looks
                  // active" unconditionally, on the assumption that cancellation can only be
                  // initiated through this terminal's own handleVoid flow. That's false — the
                  // Mobile App cancels the same way (there is no separate WS event for it, only
                  // this same ORDER_UPSERT), so a genuine Mobile-App cancellation for a line this
                  // POS had already fired was being discarded as if it were a stale echo, and
                  // Prepare Bill kept charging for the cancelled quantity. Now "local wins" only
                  // applies within a short window after THIS terminal itself fired that exact
                  // fbOrderLineId (see localFireTracker.js) — long enough to cover the local
                  // cancel→restore→re-fire race the guard was built for, but not so long that it
                  // keeps shadowing a real external cancellation indefinitely.
                  const currentLineByFbOrderLineId = new Map(currentItems.filter((i) => i.fbOrderLineId).map((i) => [i.fbOrderLineId, i]));
                  const liveItems = [
                    ...mergedItems.map((line) => {
                      const localLine = line.fbOrderLineId ? currentLineByFbOrderLineId.get(line.fbOrderLineId) : null;
                      const incomingLooksCancelled = line.isCanceled === "Y" || ["RC", "CO", "CL"].includes(line.orderStatus);
                      const localLooksActive = localLine?.order === "Y" && localLine?.isCanceled !== "Y";
                      const recentlyFiredHere = wasRecentlyFiredLocally(line.fbOrderLineId);
                      if (localLine && incomingLooksCancelled && localLooksActive) {
                        if (recentlyFiredHere) {
                          trace("BUG-1921", "ORDER_UPSERT cancel echo suppressed (recent local fire)", { fbOrderLineId: line.fbOrderLineId });
                          return { ...localLine, sendQty: 0, parkedItem: true };
                        }
                        trace("BUG-1921", "ORDER_UPSERT cancel applied (no recent local fire — trusting incoming)", { fbOrderLineId: line.fbOrderLineId });
                      }
                      return {
                        ...line,
                        sendQty: line.order === "Y" ? 0 : Math.max((Number(line.weight) || 0) - (Number(line.firedQty) || 0), 0),
                        parkedItem: true,
                      };
                    }),
                    ...localOnlyItems,
                  ];
                  const activeLiveItems = liveItems.filter(item => item.isCanceled !== "Y");
                  const total = activeLiveItems.reduce((sum, item) => sum + (Number(item.nettotal) || 0), 0);
                  const tax = activeLiveItems.reduce((sum, item) => sum + (Number(item.taxAmount) || 0), 0);
                  const totalQty = activeLiveItems.reduce((sum, item) => sum + (Number(item.weight) || 0), 0);
                  const discount = activeLiveItems.reduce((sum, item) => sum + (Number(item.discount) || 0), 0);
                  // [BUG-1921] Final state actually written to the live cart — this is what
                  // Prepare Bill reads. If a cancelled qty is still inflating the total, this
                  // entry pinpoints whether liveItems itself is wrong (bug still upstream of
                  // here) or the total/tax/totalQty math from activeLiveItems is wrong (bug is
                  // in this reduce, not the merge).
                  trace("BUG-1921", "ORDER_UPSERT: final live cart written", {
                    activeLiveItems: activeLiveItems.map((i) => ({ fbOrderLineId: i.fbOrderLineId, name: i.name, weight: i.weight, nettotal: i.nettotal, isCanceled: i.isCanceled })),
                    droppedAsCancelled: liveItems.filter((i) => i.isCanceled === "Y").map((i) => ({ fbOrderLineId: i.fbOrderLineId, name: i.name, weight: i.weight })),
                    total, tax, totalQty, discount,
                  });
                  setCart({
                    ...currentCart,
                    ...wsData,
                    tableNo: existingTable?.name || tableData?.table,
                    items: liveItems,
                    totalQty,
                    total: parseFloat(total.toFixed(precision)),
                    tax: parseFloat(tax.toFixed(precision)),
                    discount: parseFloat(discount.toFixed(precision)),
                    subTotal: parseFloat((total - tax).toFixed(precision)),
                    // Preserve the live session's payment state — a WS item-update must
                    // not overwrite paid/payments with stale values from the wsData snapshot.
                    paid: currentCart.paid || 0,
                    payments: currentCart.payments || [],
                  });
                } else {
                  // The update is for the table the waiter has open, but there's nothing to
                  // merge — e.g. the order closed/cancelled out from under them (wsData comes
                  // back {} for status CL/CO in safeParseWsData above) and no other IP order
                  // remains on this table either. There is no live-cart patch that makes sense
                  // here; silently doing nothing would leave the waiter staring at a cart for
                  // an order that no longer exists. Fall back to the same "return to table
                  // layout" call useCopyKOT.js uses after it changes an order out from under
                  // the open screen, so the waiter lands somewhere consistent with reality.
                  trace("BUG-mesh-table-status", "ORDER_UPSERT: open table's order emptied/closed remotely — returning to table layout", {
                    fbTableId, fbOrderId, status: message?.data?.status,
                  });
                  setShowProducts(false);
                  setKeyValue("0");
                }
              }
            } catch (error) {
              console.error("[v0] ORDER_UPSERT Error:", error);
            }
          } else {
            console.warn("[v0] [HandleMessage] Empty ORDER_UPSERT message");
          }
          break;

        case "NEW_KOT":
          if (message && message.order) {
            if (message.order?.status) {
              showKDSNotification(message.order);
            }
            try {
              const kotOrder = message.order;
              if (!kotOrder?.orderId || !Array.isArray(kotOrder.line)) {
                console.warn("[KDS] Invalid NEW_KOT payload");
                break;
              }

              // Find matching order directly instead of loading all FB orders
              const allFbOrders = await db.fbOrderData.toArray();
              const fbOrder = allFbOrders.find((order) => order?.cart?.sOrderID === kotOrder.orderId);

              if (!fbOrder) {
                console.warn("[KDS] No matching fbOrderData for orderId:", kotOrder.orderId);
                break;
              }

              const kotStatusMap = new Map();
              kotOrder.line.forEach((line) => {
                if (line?.name) {
                  kotStatusMap.set(line.name.trim(), line.status);
                }
              });

              const updatedCart = {
                ...fbOrder.cart,
                items: fbOrder.cart.items.map((item) => {
                  const newStatus = kotStatusMap.get(item.name?.trim());
                  return newStatus ? { ...item, orderStatus: newStatus } : item;
                }),
              };

              const updatedWsData = fbOrder.wsData
                ? {
                    ...fbOrder.wsData,
                    items: Array.isArray(fbOrder.wsData.items)
                      ? fbOrder.wsData.items.map((item) => {
                          const newStatus = kotStatusMap.get(item.name?.trim());
                          return newStatus ? { ...item, orderStatus: newStatus } : item;
                        })
                      : fbOrder.wsData.items,
                  }
                : fbOrder.wsData;

              await db.fbOrderData.update(fbOrder.id, {
                cart: updatedCart,
                wsData: updatedWsData,
              });

              const currentCart = useCartStore.getState().cart;

              if (currentCart?.sOrderID === kotOrder.orderId) {
                const updatedItems = currentCart.items.map((item) => {
                  const newStatus = kotStatusMap.get(item.name?.trim());
                  return newStatus && item.orderStatus !== newStatus ? { ...item, orderStatus: newStatus } : item;
                });

                setCart((prev) => ({
                  ...prev,
                  items: updatedItems,
                }));
              }
            } catch (err) {
              console.error("[KDS] NEW_KOT handling failed", err);
            }
          } else {
            console.warn("[v0] [HandleMessage] NEW_KOT message missing order data");
          }
          break;

        case "POS_ORDER_UPSERT":
          if (message && message.data) {
            try {
              const username = useAuthStore.getState().username;
              const salesReps = tillData?.tillAccess?.csBunit?.salesRep || [];
              const matchedRep = salesReps.find((rep) => rep.name === username);

              if (matchedRep) {
                console.debug("[POS_ORDER_UPSERT] This is a Waiter POS, skipping order sync logic");
                break;
              }

              console.debug("[POS_ORDER_UPSERT] This is a Sales POS, processing order...");

              const serverData = message.data;
              const tillId = tillData.tillAccess.cwrTill.cwrTillID;
              const tillSession = useSessionStore.getState().tillSession || {};
              const saleTypeData = usePOSConfigStore.getState().saleTypeData || {};

              // A mirror row is only ever meant for an order this terminal did NOT originate.
              // cw-socket publishes to every subscriber on the bunit, including the till that
              // punched the order, so on a single-till store EVERY event is our own echo coming
              // back ~1s later. Writing a mirror row for it overwrites the good local row the
              // punch just saved (same sOrderID = same Dexie primary key), replacing it with
              // dineIn:"N" / no tableNo / stub saleType / isSynced:1 — after which the bill can
              // never be settled and is silently lost.
              //
              // Production evidence (SECRES01 till backup + dadus_prod, 11-14 Sep): all 39 mirror
              // rows were lost, all 39 were punched by THIS till, and each has exactly one
              // pre-save-snapshot + consume in sequenceAuditLog where a settled order has two —
              // i.e. the punch saved, then the echo clobbered it before the settle pass could run.
              // e.g. SCRS/6306: pre-save-snapshot 15:46:03, mirror row written 15:46:04.
              if (serverData.cwrTillId && tillId && serverData.cwrTillId === tillId) {
                console.debug("[POS_ORDER_UPSERT] Own echo for this till, skipping:", serverData.documentno, serverData.sOrderID);
                break;
              }

              const existingOrder = await db.orders.where("sOrderID").equals(serverData.sOrderID).first();
              if (existingOrder) {
                console.debug("[POS_ORDER_UPSERT] Order already exists, skipping:", serverData.sOrderID);
                break;
              }

              // Only fetch products referenced by this order's lines (avoids loading 35K+ products)
              const lineProductIds = (serverData.line || []).map((l) => l.mProductId).filter(Boolean);
              const matchedProducts = lineProductIds.length > 0 ? await db.products.where("mProductId").anyOf(lineProductIds).toArray() : [];
              const productMap = new Map(matchedProducts.map((p) => [p.mProductId, p]));

              const openPaymentsData = await db.paymentsData.where("tillStatus").equals("open").first();
              const paymentMethodsList = openPaymentsData?.payments || [];
              const paymentMethodMap = new Map(paymentMethodsList.map((p) => [p.finPaymentmethodId, p]));

              const formatDate = (dateStr) => {
                if (!dateStr) return "";
                const date = new Date(dateStr);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                return `${day}-${month}-${year}`;
              };

              const formatDateTime = (dateStr) => {
                if (!dateStr) return "";
                const date = new Date(dateStr);
                const day = String(date.getDate()).padStart(2, "0");
                const month = String(date.getMonth() + 1).padStart(2, "0");
                const year = date.getFullYear();
                const hours = String(date.getHours()).padStart(2, "0");
                const minutes = String(date.getMinutes()).padStart(2, "0");
                const seconds = String(date.getSeconds()).padStart(2, "0");
                return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
              };

              const numberToWordsLocal = (num) => {
                const ones = [
                  "",
                  "One",
                  "Two",
                  "Three",
                  "Four",
                  "Five",
                  "Six",
                  "Seven",
                  "Eight",
                  "Nine",
                  "Ten",
                  "Eleven",
                  "Twelve",
                  "Thirteen",
                  "Fourteen",
                  "Fifteen",
                  "Sixteen",
                  "Seventeen",
                  "Eighteen",
                  "Nineteen",
                ];
                const tens = ["", "", "Twenty", "Thirty", "Forty", "Fifty", "Sixty", "Seventy", "Eighty", "Ninety"];
                if (num === 0) return "Zero Rupees Only";
                const n = Math.floor(num);
                if (n < 20) return ones[n] + " Rupees Only";
                if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? " " + ones[n % 10] : "") + " Rupees Only";
                if (n < 1000) return ones[Math.floor(n / 100)] + " Hundred" + (n % 100 !== 0 ? " " + numberToWordsLocal(n % 100).replace(" Rupees Only", "") : "") + " Rupees Only";
                if (n < 100000)
                  return (
                    numberToWordsLocal(Math.floor(n / 1000)).replace(" Rupees Only", "") +
                    " Thousand" +
                    (n % 1000 !== 0 ? " " + numberToWordsLocal(n % 1000).replace(" Rupees Only", "") : "") +
                    " Rupees Only"
                  );
                return num + " Rupees Only";
              };

              const items = (serverData.line || []).map((line, index) => {
                const product = productMap.get(line.mProductId) || {};
                return {
                  productId: line.mProductId,
                  name: product.name || line.description || `Product ${index + 1}`,
                  name2: product.name2 || "",
                  value: product.value || line.mProductId,
                  description: product.description || "",
                  imageUrl: product.imageUrl || "",
                  imageurl: product.imageurl || "",
                  hsncode: product.hsncode || null,
                  shortDescription: product.shortDescription || "",
                  upc: product.upc || "",
                  tax: line.csTaxId,
                  taxAmount: line.linetax,
                  taxRate: product.taxRate || (line.linenet > 0 ? Math.round((line.linetax / line.linenet) * 100) : 0),
                  taxCategory: product.taxCategory || "",
                  taxName: product.taxName || "",
                  taxFlag: product.taxFlag || "N",
                  unitTax: line.unittax || line.linetax,
                  stdTax: line.linestd || 0,
                  nettotal: line.linegross || line.linenet + line.linetax,
                  linegross: line.linegross,
                  linenet: line.linenet,
                  linestd: line.linestd,
                  linetax: line.linetax,
                  netunit: line.netunit,
                  listprice: line.listprice,
                  listPrice: line.listprice,
                  grossunit: line.grossunit,
                  grossstd: line.grossstd,
                  grosslist: line.grosslist,
                  unitPrice: line.unitprice,
                  sunitprice: line.unitprice,
                  netList: line.netlist || line.linenet,
                  netStd: line.netstd || line.linenet,
                  originalPrice: line.listprice,
                  realPrice: line.listprice,
                  salePrice: line.listprice,
                  weight: line.qty || 1,
                  returnQty: line.returnQty || null,
                  uom: product.uom || line.csUomId,
                  uom_name: product.uom_name || "Pieces",
                  uomData: product.uomData || [],
                  priceList: product.priceList || [],
                  mProductCategoryId: product.mProductCategoryId || "",
                  productCategoryName: product.productCategoryName || "",
                  productBrandId: product.productBrandId || null,
                  productBrandName: product.productBrandName || null,
                  productManufacturerId: product.productManufacturerId || null,
                  productSegment: product.productSegment || "",
                  productionCenter: product.productionCenter || "",
                  productionCenterId: product.productionCenterId || "",
                  itemType: product.itemType || "FGD",
                  type: product.type || "I",
                  foodType: product.foodType || "",
                  batchedForSale: product.batchedForSale || "N",
                  batchedForStock: product.batchedForStock || "N",
                  batchedProduct: product.batchedProduct || "N",
                  batchno: line.batchno || null,
                  mBatchId: line.mBatchId || null,
                  isReturn: line.returnline || false,
                  isDecimal: product.isDecimal || false,
                  isDecimalQty: product.isDecimalQty || false,
                  isManualQty: product.isManualQty || false,
                  restrictPosQtyEdit: product.restrictPosQtyEdit || "N",
                  isPromoApplicable: product.isPromoApplicable || false,
                  // BUG-1864: isQtyDesimal is a precision digit count (0 = unit-based product is
                  // a valid value) — `||` would wrongly promote it to 2, poisoning every
                  // downstream qty-rounding site that trusts this field.
                  isQtyDesimal: product.isQtyDesimal ?? 2,
                  multiPrice: product.multiPrice || "N",
                  posPriceEdit: product.posPriceEdit || "N",
                  addNewLine: product.addNewLine || "N",
                  order: product.order || "N",
                  showAddons: product.showAddons || "N",
                  showAlternateUomInPos: product.showAlternateUomInPos || "N",
                  interTaxId: product?.interTaxId || "",
                  interTaxRate: product?.interTaxRate || 0,
                  interTaxName: product?.interTaxName || "",
                  discount: line.discount || 0,
                  discountName: line.discountName || "",
                  mPricingruleId: line.mPricingruleId || null,
                  upcPricingRule: product.upcPricingRule || null,
                  lineId: line.sOrderlineID,
                  key: index,
                  csBunitId: line.csBunitId || null,
                  bunitName: product.bunitName || "",
                  cwrPcatalogueId: product.cwrPcatalogueId || "",
                  alternateUom: product.alternateUom || null,
                  companion: product?.companion || "N",
                  customAttributes: product.customAttributes || [],
                  newCustomAttributes: product.newCustomAttributes || [],
                  cwrMenuTimeslot: product.cwrMenuTimeslot || [],
                  productAddons: product.productAddons || [],
                  selectedAddons: [],
                  primaryOrderLine: line.primaryOrderLine || null,
                  printerId: product.printerId || "",
                  preparationTime: product.preparationTime || "",
                  shelfLife: product.shelfLife || "",
                  stock: product.stock || 0,
                  salesRepId: line.salesRepId || null,
                  salesRepName: line.salesRepName || "",
                  saleType: saleTypeData?.value || "",
                };
              });

              const payments = (serverData.payments || []).map((payment) => {
                const matchedMethod = paymentMethodMap.get(payment.finPaymentmethodID);
                return {
                  finPaymentmethodId: payment.finPaymentmethodID,
                  cWRPaymentMethodID: matchedMethod?.cWRPaymentMethodID || "",
                  name: matchedMethod?.name || "Cash",
                  cwrTillId: tillId,
                  finFinancialAccountId: matchedMethod?.finFinancialAccountId || "",
                  finDayCloseAccountId: matchedMethod?.finDayCloseAccountId || null,
                  csCurrencyId: matchedMethod?.csCurrencyId || null,
                  isoCode: matchedMethod?.isoCode || null,
                  isDefault: matchedMethod?.isDefault || "N",
                  isactive: matchedMethod?.isactive ?? true,
                  integratedPayment: matchedMethod?.integratedPayment || false,
                  paymentProvider: matchedMethod?.paymentProvider || null,
                  paymentProviderDetails: matchedMethod?.paymentProviderDetails || null,
                  roundOffForPos: matchedMethod?.roundOffForPos || "Y",
                  sequenceNo: matchedMethod?.sequenceNo || null,
                  salesType: matchedMethod?.salesType || [],
                  saleTypeId: matchedMethod?.saleTypeId || null,
                  saleTypeName: matchedMethod?.saleTypeName || null,
                  iscredit: matchedMethod?.iscredit || false,
                  isloyalty: matchedMethod?.isloyalty || false,
                  isGiftCard: matchedMethod?.isGiftCard || false,
                  isGiftVoucher: matchedMethod?.isGiftVoucher || false,
                  isCreditNote: matchedMethod?.isCreditNote || false,
                  amount: String(payment.amount),
                };
              });

              const taxDetails = (serverData.tax || []).map((t) => {
                const matchedProduct = matchedProducts.find((p) => p.cTaxId === t.csTaxID);
                return {
                  csTaxID: t.csTaxID,
                  taxableAmt: t.taxableAmt,
                  taxAmt: t.taxAmt,
                  taxRate: matchedProduct?.taxRate?.toString() || (t.taxableAmt > 0 ? String(Math.round((t.taxAmt / t.taxableAmt) * 100)) : "0"),
                  taxCategory: matchedProduct?.taxCategory || "",
                  taxName: matchedProduct?.taxName || "",
                };
              });

              const customer = {
                b2cCustomerId: serverData.cwrB2cCustomerId || "",
                cwrCustomerId: serverData.cwrB2cCustomerId || "",
                b2cRegisteredstoreId: "0",
                code: "",
                name: "",
                email: null,
                mobileNo: null,
                pincode: 0,
                iscredit: false,
                balancePoints: null,
                retlLoyaltyBalance: 0,
                priceListName: null,
                sPriceListId: null,
                sCustomer: { sCustomerID: serverData.sCustomerId || "" },
                sCustomerId: serverData.sCustomerId || "",
                sCustomerBillingId: serverData.sCustomerBillingId || "",
                sCustomerShippingId: serverData.sCustomerShippingId || "",
              };

              const saleType = {
                cwrSaletypeId: serverData.cwrSaletypeId || saleTypeData?.cwrSaletypeId || "",
                value: saleTypeData?.value || "",
                name: saleTypeData?.name || "",
                isdefault: saleTypeData?.isdefault || "N",
                colourCode: saleTypeData?.colourCode || "",
                enableKot: saleTypeData?.enableKot || "N",
                enableLayaway: saleTypeData?.enableLayaway || "N",
              };

              const isReturn = serverData.isReturn === "Y" || serverData.isReturn === true;
              const layAway = serverData.layAway || "N";
              // Actual settled amount from the payments the Waiter POS collected —
              // NOT serverData.orderTotal. Forcing paid === orderTotal made a genuinely
              // partially-paid advance/layaway order look fully settled, which
              // useOrderProcessing.js (paidAmount + advancePayment >= totalAmount) relies
              // on to decide whether a layaway order is actually complete.
              const actualPaid = roundTo(
                (serverData.payments || []).reduce((sum, p) => sum + (parseFloat(p.amount) || 0), 0),
                precision
              );

              const cartToDb = {
                ...serverData,
                items,
                payments,
                taxDetails,
                combinedTaxRates: serverData.tax || [],
                total: serverData.orderTotal,
                tax: serverData.taxamt,
                paid: actualPaid,
                tenderedAmount: actualPaid,
                subTotal: serverData.nettotal || 0,
                totalStd: serverData.orderTotal,
                totalQty: serverData.cwrProductQty || items.length,
                accumulationPoints: serverData.accumulation || 0,
                redemptionPoints: serverData.redemption || 0,
                discount: serverData.discAmount || 0,
                roundOff: serverData.roundoff || 0,
                creditAmount: parseFloat(serverData.creditAmount) || 0,
                createdBy: serverData.createdby || "",
                orderDate: formatDate(serverData.dateordered),
                orderTime: formatDateTime(serverData.created),
                orderTimeDetails: {
                  orderStartTime: formatDateTime(serverData.created),
                  orderEndTime: formatDateTime(serverData.updated),
                  paymentStartTime: formatDateTime(serverData.created),
                },
                customer,
                saleType,
                tillSessionId: tillSession.tillSessionId || "",
                tillName: tillData?.tillAccess?.cwrTill?.till || "",
                userName: username || "",
                isReturn,
                // BUG: layAway (advance) orders must route to "Advance" like the rest of the
                // codebase expects (see useOrderProcessing.js:757, advanceOrderValidation.js) —
                // hardcoding "Invoice" here mis-routed partially-paid advance orders.
                docType: isReturn ? "Return" : layAway === "Y" ? "Advance" : "Invoice",
                // Mirrors useOrderProcessing.js:757's orderCompleted derivation so a layaway
                // order isn't marked complete before it's actually settled.
                orderCompleted: layAway !== "Y",
                // isSynced starts at 1 (NOT pending) on purpose: posOrderProcessor1 is a
                // blind CREATE, not idempotent by sOrderID (production trace BHRS/1781 ->
                // BHRS/1785, see useOrderProcessing.js isPrepareBillCancelled comment) -- if
                // this mirror device also ran the normal isSynced:0 pending-sync loop, and the
                // owning device's own sync was merely slow (not lost), both devices would call
                // posOrderProcessor1 for the same order and mint two separate server documents.
                // Instead this order is flagged mirrorOrder/mirrorVerifyAfter below; DI-36
                // (verifyMirrorOrders, dataIntegrity.js) checks back after the grace period via
                // the read-only salesHistoryDetails lookup and only then promotes it to
                // isSynced: 0 if the owner genuinely never synced it -- see DI-36 doc comment
                // for the full reasoning, including why a flat isSynced:1 (no verification) is
                // NOT safe either: it silently strands the order forever if the owner never syncs.
                // Do NOT reintroduce a payments.length-based guard here either -- it previously
                // misclassified credit sales (payments:[] by design), B2B payment-terms orders,
                // and gift-card/loyalty-only settlements as "still owes settlement".
                isSynced: 1,
                mirrorOrder: true,
                mirrorVerifyAfter: new Date(Date.now() + 30000).toISOString(),
                mirrorVerified: false,
                syncAttempts: 0,
                key: serverData.sOrderID,
                orderType: serverData.cwrSaletypeId || saleTypeData?.cwrSaletypeId || "",
                referenceId: serverData.redeemRefId || serverData.creditReferenceNo || "",
                couponRefId: serverData.creditReferenceNo || "",
                giftCardRefId: "",
                amountInWords: numberToWordsLocal(serverData.orderTotal),
                dineIn: serverData.dineIn || "N",
                layAway,
                // Till-close reports filter layaway orders on `layAway === "Y" && statusType ===
                // "layAway"` together (CloseTIllAll.js, tillCloseCWHCprinter.js) — hardcoding "N"
                // here made every WS-synced advance order invisible to those reports.
                statusType: layAway === "Y" ? "layAway" : "N",
                typeOrder: "N",
                iscoupon: false,
                totalBillDicount: false,
                totalDiscountFlag: serverData.discAmount > 0,
                overPayedAmount: 0,
                advancePayment: 0,
                change: 0,
                cardPaymentData: {},
                couponInput: [],
                giftCardData: [],
                groupedItemsByTaxRate: {},
                itemsSummery: {},
                customerSearchKey: customer.code || "",
              };

              const paymentDetails = serverData.metaData?.find((m) => m.key === "Payment Details");
              if (paymentDetails?.value) {
                try {
                  const paymentArr = JSON.parse(paymentDetails.value);
                  const changeObj = paymentArr.find((p) => p.hasOwnProperty("change"));
                  cartToDb.change = changeObj?.change || 0;
                } catch (e) {
                  cartToDb.change = 0;
                }
              }

              console.debug("[POS_ORDER_UPSERT] Transformed cartToDb:", cartToDb);

              // Re-check immediately before the write. The existence check above runs at the top
              // of this handler, several awaits earlier (product lookup, paymentsData lookup, cart
              // assembly) — a plain read-then-write is not atomic, and the owning device's own
              // db.orders.put (processOrderApproved, useOrderProcessing.js ~L1570) can land inside
              // that window, after which this put would silently overwrite it. Same reasoning and
              // same remedy as checkAndBlockDuplicateEstimate's "Late" re-check in
              // useOrderProcessing.js — see its comment. This shrinks the window to this one
              // statement instead of the whole handler body.
              const raced = await db.orders.where("sOrderID").equals(serverData.sOrderID).first();
              if (raced) {
                console.debug("[POS_ORDER_UPSERT] Order appeared while building mirror row, not overwriting:", serverData.sOrderID);
                break;
              }

              await db.orders.put(cartToDb);
              // DI-36 reads tracked mirror rows by key instead of scanning db.orders every tick.
              trackPendingMirrorOrder(db, cartToDb.sOrderID);
              console.debug("[POS_ORDER_UPSERT] Added new order:", cartToDb.sOrderID);

              await db.ordersData
                .where("tillStatus")
                .equals("open")
                .modify((existingData) => {
                  for (const item of cartToDb.items) {
                    const index = existingData.items.findIndex((ele) => ele.value === item.value);
                    if (index >= 0) {
                      existingData.items[index].taxAmount = +parseFloat((existingData.items[index].taxAmount + (item.taxAmount || 0)).toFixed(precision));
                      existingData.items[index].nettotal = +parseFloat((existingData.items[index].nettotal + (item.nettotal || 0)).toFixed(precision));
                      existingData.items[index].weight = parseFloat(
                        (parseFloat(existingData.items[index].weight) + parseFloat(item.weight || 0)).toFixed(existingData.items[index].isQtyDesimal ?? 2)
                      );
                    } else {
                      existingData.items.push({
                        taxAmount: +parseFloat((item.taxAmount || 0).toFixed(precision)),
                        nettotal: +parseFloat((item.nettotal || 0).toFixed(precision)),
                        weight: parseFloat((item.weight || 0).toFixed(item.isQtyDesimal ?? 2)),
                        value: item.value,
                        name: item.name,
                      });
                    }
                  }

                  const targetTaxArray = isReturn ? existingData.returnTax : existingData.salesTax;
                  for (const newTax of cartToDb.taxDetails || []) {
                    const existingTax = targetTaxArray.find((tax) => tax.taxRate === newTax.taxRate);
                    if (existingTax) {
                      existingTax.taxAmt = +parseFloat((existingTax.taxAmt + (newTax.taxAmt || 0)).toFixed(precision));
                      existingTax.taxableAmt = +parseFloat((existingTax.taxableAmt + (newTax.taxableAmt || 0)).toFixed(precision));
                    } else {
                      targetTaxArray.push({
                        csTaxID: newTax.csTaxID,
                        taxableAmt: parseFloat((newTax.taxableAmt || 0).toFixed(precision)),
                        taxAmt: parseFloat((newTax.taxAmt || 0).toFixed(precision)),
                        taxRate: newTax.taxRate,
                        taxCategory: newTax.taxCategory,
                        taxName: newTax.taxName,
                      });
                    }
                  }

                  if (isReturn) {
                    existingData.netReturnTotal = +parseFloat(((existingData.netReturnTotal || 0) + Math.abs(cartToDb.total - cartToDb.tax)).toFixed(precision));
                    existingData.grossReturnTotal = +parseFloat(((existingData.grossReturnTotal || 0) + Math.abs(cartToDb.total)).toFixed(precision));
                    existingData.totaReturnlTax = +parseFloat(((existingData.totaReturnlTax || 0) + Math.abs(cartToDb.tax)).toFixed(precision));
                    existingData.returnsTransactions = (existingData.returnsTransactions || 0) + 1;
                  } else {
                    existingData.netTotal = +parseFloat(((existingData.netTotal || 0) + (cartToDb.total - cartToDb.tax)).toFixed(precision));
                    existingData.grossTotal = +parseFloat(((existingData.grossTotal || 0) + cartToDb.total).toFixed(precision));
                    existingData.totalTax = +parseFloat(((existingData.totalTax || 0) + cartToDb.tax).toFixed(precision));
                    existingData.salesTransactions = (existingData.salesTransactions || 0) + 1;
                  }
                });

              await db.paymentsData
                .where("tillStatus")
                .equals("open")
                .modify((event) => {
                  const filteredPayments = event.payments.filter((payment) => payment?.cwrTillId === tillId);
                  for (const payment of filteredPayments) {
                    let currPaymentAmount = 0;
                    let currPaymentReturn = 0;
                    for (const orderPayment of cartToDb.payments || []) {
                      if (payment.finPaymentmethodId === orderPayment.finPaymentmethodId) {
                        if (cartToDb.total > 0) {
                          currPaymentAmount += parseFloat(orderPayment.amount);
                          payment.transactionCount = (payment.transactionCount || 0) + 1;
                        } else if (cartToDb.total < 0) {
                          currPaymentReturn += parseFloat(Math.abs(orderPayment.amount));
                          payment.transactionCount = (payment.transactionCount || 0) + 1;
                        }
                      }
                    }
                    payment.expectedAmount = (payment.expectedAmount || 0) + currPaymentAmount;
                    payment.amount = (payment.amount || 0) + currPaymentAmount;
                    payment.paymentReturn = (payment.paymentReturn || 0) + currPaymentReturn;
                    payment.difference = (payment.difference || 0) + payment.expectedAmount;
                    payment.change = (payment.change || 0) + (cartToDb.change || 0);
                  }
                  event.payments = filteredPayments;

                  // [DI-09] Link payment records to their order via sOrderID for audit traceability
                  if (!event.orderPayments) event.orderPayments = [];
                  event.orderPayments.push({
                    sOrderID: cartToDb.sOrderID,
                    documentno: cartToDb.documentno,
                    orderTotal: cartToDb.total,
                    payments: (cartToDb.payments || []).map((p) => ({
                      name: p.name,
                      amount: parseFloat(p.amount) || 0,
                      amountTendered: p.amountTendered != null ? parseFloat(p.amountTendered) : undefined,
                      finPaymentmethodId: p.finPaymentmethodId,
                    })),
                    change: cartToDb.change || 0,
                    timestamp: new Date().toISOString(),
                  });
                });

              console.debug("[POS_ORDER_UPSERT] Successfully processed waiter order:", serverData.documentno);
            } catch (err) {
              console.error("[POS_ORDER_UPSERT] Error processing order:", err);
            }
          } else {
            console.warn("[HandleMessage] POS_ORDER_UPSERT message missing order data");
          }
          break;

        case "MISSED_EVENTS_COMPLETE":
          console.debug(`[v0] [HandleMessage] Missed table status events sync complete. Total: ${message.count}`);
          break;

        case "REDIS_RECONNECTED":
          console.debug("[v0] [HandleMessage] Redis reconnected - table status will sync automatically");
          break;

        case "ERROR":
          console.error("[v0] [HandleMessage] WebSocket error message:", message.error || message.message);
          break;

        default:
          console.debug("[v0] [HandleMessage] Unknown message type:", message.type);
      }
    } catch (error) {
      console.error("[v0] [HandleMessage] Error handling WebSocket message:", error);
      console.error("[v0] [HandleMessage] Stack trace:", error.stack);
    }

    console.debug("[v0] [HandleMessage] ========== MESSAGE PROCESSING DONE ==========");
  }, []);

  const { addRawMessageListener } = useWebSocket();

  useEffect(() => {
    const unsubscribe = addRawMessageListener(handleWebSocketMessage);
    return unsubscribe;
  }, [addRawMessageListener, handleWebSocketMessage]);

  return { handleWebSocketMessage };
};

export default useWebSocketHandler;
